import type { Book, Chapter, ChapterPages, ChapterSummary } from '@wellread/api';
import { useCallback } from 'react';
import { createStore, get, set } from 'idb-keyval';
import { call, client, keys, queryClient } from './api';
import { useContents } from './queries';

// Chapter HTML never changes after ingest, so it is cached in IndexedDB
// indefinitely: a cold open reads from disk, not the network.
const store = createStore('wellread-chapters', 'chapters');

export async function fetchChapter(bookId: string, idx: number): Promise<Chapter> {
  const key = `${bookId}:${idx}`;
  const cached = await get<Chapter>(key, store).catch(() => undefined);
  if (cached) return cached;
  const ch = await call(
    client.GET('/v1/books/{id}/chapters/{idx}', { params: { path: { id: bookId, idx } } }),
  );
  void set(key, ch, store).catch(() => undefined);
  return ch;
}

export function chapterQuery(bookId: string, idx: number) {
  return {
    queryKey: keys.chapter(bookId, idx),
    queryFn: () => fetchChapter(bookId, idx),
    staleTime: Infinity,
  };
}

/**
 * Where a PDF book's chapter is printed, for the original view. A chapter
 * the file no longer reads the same way has none (404): it shows as text.
 */
export function printedQuery(bookId: string, idx: number) {
  return {
    queryKey: keys.printed(bookId, idx),
    queryFn: async (): Promise<ChapterPages | null> => {
      const { data, response } = await client.GET('/v1/books/{id}/chapters/{idx}/pages', {
        params: { path: { id: bookId, idx } },
      });
      if (response.status === 404) return null;
      if (!data) throw new Error(response.statusText);
      return data;
    },
    staleTime: Infinity,
  };
}

/** A printed page of a PDF book, drawn: a data URI. */
export function fetchPage(bookId: string, n: number): Promise<string> {
  return queryClient.fetchQuery({
    queryKey: keys.page(bookId, n),
    queryFn: async () =>
      (await call(client.GET('/v1/books/{id}/pages/{n}', { params: { path: { id: bookId, n } } }))).image,
    staleTime: Infinity,
  });
}

/** Warms the next chapter so turning to it never waits. */
export function prefetchChapter(bookId: string, idx: number): void {
  void queryClient.prefetchQuery(chapterQuery(bookId, idx));
}

type Named = Pick<ChapterSummary, 'title' | 'number'>;

/** A numbered chapter's title without the number it repeats: "1. Replication" → "Replication". */
export function bareTitle(c: Named): string {
  if (!c.number) return c.title;
  const t = c.title.replace(/^\s*(chapter\s+)?([0-9]+|[ivxlcdm]+)\s*[.:\-–—]\s*/i, '').trim();
  return t || c.title;
}

/**
 * A cited place, as every screen prints one: "Chapter 1, Why keep copies",
 * or the title alone where the book doesn't number it ("Preface"). A title
 * that repeats its number ("1. Why keep copies") says it once. Either may be
 * missing from a payload cached before the number was sent.
 */
export function citedPlace(number: number | undefined, title: string | undefined): string {
  title = (title ?? '').trim();
  if (!number) return title;
  const bare = bareTitle({ number, title });
  return bare && !/^chapter\s+\S+$/i.test(bare) ? `Chapter ${number}, ${bare}` : `Chapter ${number}`;
}

/**
 * How the chrome names a chapter: "Chapter 3" (short "ch.3") when the book
 * numbers it, its own title ("Preface", "Glossary") when it doesn't. Until
 * the contents arrive, by position.
 */
export function chapterName(c: Named | undefined, idx: number, short = false): string {
  if (!c) return short ? `ch.${idx + 1}` : `Chapter ${idx + 1}`;
  if (c.number) return short ? `ch.${c.number}` : `Chapter ${c.number}`;
  return c.title;
}

/** chapterName for one book, by chapter index. */
export function useChapterNames(bookId: string | undefined): (idx: number, short?: boolean) => string {
  const contents = useContents(bookId ?? '', !!bookId);
  const list = contents.data?.chapters;
  return useCallback((idx: number, short = false) => chapterName(list?.[idx], idx, short), [list]);
}

/** Where the reader is, from the book alone: "Chapter 6 of 14", "Preface", or "Not started". */
export function placeName(b: Book): string {
  const p = b.position;
  if (!p) return 'Not started';
  if (p.chapter_number) return `Chapter ${p.chapter_number} of ${b.numbered}`;
  return p.chapter_title || `Chapter ${p.chapter_idx + 1}`;
}
